import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, User, Search, Filter } from 'lucide-react';
import { Driver } from '../../types';
import { driverApi } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';

const DriverManagement: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await driverApi.getAll();
      if (response.success && response.data) {
        setDrivers(response.data);
      }
    } catch (err) {
      setError('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  // const handleAddDriver = async (driverData: Partial<Driver>) => {
  //   try {
  //     const response = await driverApi.create(driverData);
  //     if (response.success && response.data) {
  //       setDrivers([...drivers, response.data]);
  //       setShowAddModal(false);
  //     }
  //   } catch (err) {
  //     setError('Failed to add driver');
  //   }
  // };

  // const handleEditDriver = async (driverData: Partial<Driver>) => {
  //   if (!editingDriver) return;
  //   
  //   try {
  //     const response = await driverApi.update(editingDriver.id, driverData);
  //     if (response.success && response.data) {
  //       setDrivers(drivers.map(d => d.id === editingDriver.id ? response.data! : d));
  //       setEditingDriver(null);
  //     }
  //   } catch (err) {
  //     setError('Failed to update driver');
  //   }
  // };

  const handleDeleteDriver = async (id: number) => {
    if (!confirm('Are you sure you want to delete this driver?')) return;
    
    try {
      const response = await driverApi.delete(id);
      if (response.success) {
        setDrivers(drivers.filter(d => d.id !== id));
      }
    } catch (err) {
      setError('Failed to delete driver');
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         driver.license_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || driver.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-fleet-dark">Driver Management</h1>
          <p className="text-fleet-neutral">Manage drivers and sync with Traccar system</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Driver
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-fleet p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fleet-neutral h-5 w-5" />
              <input
                type="text"
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-fleet-neutral" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Drivers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDrivers.map((driver) => (
          <div key={driver.id} className="bg-white rounded-lg shadow-fleet p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="h-12 w-12 bg-fleet-primary bg-opacity-10 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-fleet-primary" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-fleet-dark">{driver.name}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(driver.status)}`}>
                    {driver.status}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingDriver(driver)}
                  className="text-fleet-primary hover:text-fleet-secondary"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteDriver(driver.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-fleet-neutral">
                <span className="font-medium">Email:</span>
                <span className="ml-2">{driver.email}</span>
              </div>
              <div className="flex items-center text-sm text-fleet-neutral">
                <span className="font-medium">Phone:</span>
                <span className="ml-2">{driver.phone}</span>
              </div>
              <div className="flex items-center text-sm text-fleet-neutral">
                <span className="font-medium">License:</span>
                <span className="ml-2">{driver.license_number}</span>
              </div>
              {driver.traccar_driver_id && (
                <div className="flex items-center text-sm text-fleet-neutral">
                  <span className="font-medium">Traccar ID:</span>
                  <span className="ml-2">{driver.traccar_driver_id}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Driver Form Modal - simplified for demo */}
      {(showAddModal || editingDriver) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-fleet-dark mb-4">
                {editingDriver ? 'Edit Driver' : 'Add New Driver'}
              </h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Driver Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
                <input
                  type="email"
                  placeholder="Email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="License Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingDriver(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Handle form submission here
                    setShowAddModal(false);
                    setEditingDriver(null);
                  }}
                  className="px-4 py-2 bg-fleet-primary text-white rounded-lg hover:bg-fleet-secondary"
                >
                  {editingDriver ? 'Update' : 'Add'} Driver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverManagement;